/**
 * Screener decision-tree router.
 *
 * Pure-function `routeScreener(answers)` that walks the answer set
 * in the order encoded by `screenerQuestions` and decides the next
 * question or terminal outcome. The router does NOT mutate, does
 * NOT call into the form engine, and does NOT depend on React. The
 * UI shell wraps it; the integration tests drive it directly.
 *
 * Decision tree (precedence order — first match wins):
 *
 *   1. Path qualifiers — `refer_different_form`
 *      • filing_path != spouse_of_us_citizen
 *      • inside_united_states != inside
 *      • derivative_or_primary == derivative
 *      • beneficiary_immigration_status ∈
 *           { asylee_or_refugee, entered_without_inspection }
 *      • sponsor_citizenship == lpr
 *
 *   2. Hard disqualifiers — `ineligible_removal` / `ineligible_criminal`
 *      • immigration_history_bundle ⊇ {final_removal_order |
 *           reinstated_removal_order}                → ineligible_removal
 *      • criminal_public_charge_bundle ⊇ {drug_trafficking |
 *           human_trafficking | terrorist_group_member}
 *                                                    → ineligible_criminal
 *      • severe_harm_bundle ⊇ {killed | severely_injured |
 *           torture_or_genocide | terrorist_activity}
 *                                                    → ineligible_criminal
 *
 *   3. Strong disqualifiers — `refer_attorney`
 *      • immigration_history_bundle ⊇ {removal_proceedings |
 *           unlawful_presence_over_one_year | misrepresentation}
 *      • criminal_public_charge_bundle ⊇ {arrested_or_detained |
 *           convicted | public_charge}
 *
 *   4. Path-different qualifiers — `refer_attorney`
 *      • j1_two_year_rule == true AND
 *          j1_two_year_complied ∈ { 'no' }
 *
 *   5. If `sponsor_citizenship == unsure` or
 *      `sponsor_income_bracket == unsure` we still continue to next
 *      question — these are soft signals, not hard gates. The
 *      eligible-outcome page surfaces them as "things to confirm
 *      with your sponsor" without blocking the funnel.
 *
 *   6. All questions answered with no terminal hit → `eligible`.
 *
 * Property-style guarantee: every outcome is reachable from the
 * 22-question config (the unit tests below construct one positive
 * answer set per outcome).
 */
import {
  screenerQuestions,
  type IScreenerQuestion,
  type ScreenerOutcomeId,
} from './screenerConfig';

/**
 * Possible answer values keyed by question id. The router accepts
 * a `Partial` so callers can route mid-flow; `undefined` means
 * unanswered.
 */
export interface IScreenerAnswers {
  readonly filing_path?: string;
  readonly inside_united_states?: string;
  readonly beneficiary_immigration_status?: string;
  readonly derivative_or_primary?: string;
  readonly sponsor_citizenship?: string;
  readonly sponsor_income_bracket?: string;
  readonly has_marriage_certificate?: boolean;
  readonly j1_two_year_rule?: boolean;
  readonly j1_two_year_complied?: string;
  // 12 legacy boolean bar-question keys
  // (removal_final_order, removal_reinstated, criminal_drug_trafficking,
  // criminal_human_trafficking, security_terrorist_membership,
  // removal_in_proceedings, criminal_arrested, criminal_convicted,
  // public_charge_subject, security_committed_terrorist_activity,
  // unlawful_presence_over_one_year, immigration_misrepresentation)
  // collapse into two multi-select bundles below. The handoff
  // (screenerHandoff.ts) fans the array values back into the same
  // catalog booleans so the intake stays unchanged.
  readonly immigration_history_bundle?: readonly string[];
  readonly criminal_public_charge_bundle?: readonly string[];
  readonly security_severe_harm_bundle?: readonly string[];
}

/**
 * The router's decision. Either advances to a specific question
 * (`nextQuestionId`) or terminates with an `outcome` and a
 * reason that the outcome page surfaces in copy.
 */
export type ScreenerDecision =
  | {
      readonly kind: 'continue';
      readonly nextQuestionId: string;
    }
  | {
      readonly kind: 'outcome';
      readonly outcome: ScreenerOutcomeId;
      /**
       * Stable identifier for the rule that fired. The outcome page
       * uses this to surface a one-line "why" (e.g. "based on your
       * answer about a final removal order"). Tests assert on it.
       */
      readonly reason: string;
    };

/**
 * Severe-harm bundle entries that route to `ineligible_criminal`.
 * `none` is included in the option list but doesn't trigger.
 */
const SEVERE_HARM_HARD_VALUES: ReadonlySet<string> = new Set([
  'killed',
  'severely_injured',
  'torture_or_genocide',
  'terrorist_activity',
]);

/**
 * Bar-question consolidation routing tables. Each map
 * lists the value-string from the multi-select to the outcome the
 * router should fire when the user checks it. Hard-disqualifier
 * values come first (ineligible_*) so the router emits the strongest
 * outcome when multiple boxes are checked.
 */
const IMMIGRATION_HISTORY_HARD: ReadonlySet<string> = new Set([
  'final_removal_order',
  'reinstated_removal_order',
]);

const IMMIGRATION_HISTORY_SOFT: ReadonlySet<string> = new Set([
  'removal_proceedings',
  'unlawful_presence_over_one_year',
  'misrepresentation',
]);

const CRIMINAL_PUBLIC_CHARGE_HARD: ReadonlySet<string> = new Set([
  'drug_trafficking',
  'human_trafficking',
  'terrorist_group_member',
]);

const CRIMINAL_PUBLIC_CHARGE_SOFT: ReadonlySet<string> = new Set([
  'arrested_or_detained',
  'convicted',
  'public_charge',
]);

/**
 * Single source of truth for the rule order. Each rule pattern-matches
 * on the partial answers; the first one that fires wins. Listed here
 * (not inlined into `routeScreener`) so the property tests can
 * iterate them.
 *
 * A rule returning `undefined` means "no opinion, keep checking."
 */
type Rule = (a: IScreenerAnswers) => ScreenerDecision | undefined;

const refer = (
  outcome: ScreenerOutcomeId,
  reason: string,
): ScreenerDecision => ({
  kind: 'outcome',
  outcome,
  reason,
});

const RULES: readonly Rule[] = [
  // 1. Path qualifiers → refer_different_form
  a =>
    a.filing_path !== undefined && a.filing_path !== 'spouse_of_us_citizen'
      ? refer('refer_different_form', 'filing_path_not_spouse_of_us_citizen')
      : undefined,
  a =>
    a.inside_united_states !== undefined && a.inside_united_states !== 'inside'
      ? refer('refer_different_form', 'inside_united_states_no')
      : undefined,
  a =>
    a.derivative_or_primary === 'derivative'
      ? refer('refer_different_form', 'derivative_applicant')
      : undefined,
  a =>
    a.beneficiary_immigration_status === 'asylee_or_refugee'
      ? refer('refer_different_form', 'asylee_or_refugee_path')
      : undefined,
  a =>
    a.beneficiary_immigration_status === 'entered_without_inspection'
      ? refer('refer_attorney', 'entered_without_inspection')
      : undefined,
  a =>
    a.sponsor_citizenship === 'lpr'
      ? refer('refer_different_form', 'sponsor_is_lpr_not_usc')
      : undefined,

  // 2. Hard disqualifiers → ineligible_*
  // Bar-question consolidation. Each bundle is checked
  // for the strongest signal first (hard set → ineligible_*) then
  // the soft set (refer_attorney). Within a set the first matching
  // value wins so the reason string stays specific.
  a => {
    const bundle = a.immigration_history_bundle;
    if (!bundle) return undefined;
    const hit = bundle.find(v => IMMIGRATION_HISTORY_HARD.has(v));
    return hit
      ? refer('ineligible_removal', `immigration_history:${hit}`)
      : undefined;
  },
  a => {
    const bundle = a.criminal_public_charge_bundle;
    if (!bundle) return undefined;
    const hit = bundle.find(v => CRIMINAL_PUBLIC_CHARGE_HARD.has(v));
    return hit
      ? refer('ineligible_criminal', `criminal_public_charge:${hit}`)
      : undefined;
  },
  a => {
    if (!a.security_severe_harm_bundle) return undefined;
    const hit = a.security_severe_harm_bundle.find(v =>
      SEVERE_HARM_HARD_VALUES.has(v),
    );
    return hit
      ? refer('ineligible_criminal', `severe_harm:${hit}`)
      : undefined;
  },

  // 3. Strong disqualifiers → refer_attorney
  a => {
    const bundle = a.immigration_history_bundle;
    if (!bundle) return undefined;
    const hit = bundle.find(v => IMMIGRATION_HISTORY_SOFT.has(v));
    return hit
      ? refer('refer_attorney', `immigration_history:${hit}`)
      : undefined;
  },
  a => {
    const bundle = a.criminal_public_charge_bundle;
    if (!bundle) return undefined;
    const hit = bundle.find(v => CRIMINAL_PUBLIC_CHARGE_SOFT.has(v));
    return hit
      ? refer('refer_attorney', `criminal_public_charge:${hit}`)
      : undefined;
  },

  // 4. Path-different qualifiers
  a =>
    a.j1_two_year_rule === true && a.j1_two_year_complied === 'no'
      ? refer('refer_attorney', 'j1_two_year_unsatisfied')
      : undefined,

  // Marriage-certificate "no" is a soft signal — we don't fail the
  // user, but the eligible-outcome page tells them to come back when
  // the marriage is finalized.
];

/**
 * Walk the rule list. Returns the first terminal decision or
 * `undefined` if the answer set is fully clean.
 */
function evaluateRules(
  answers: IScreenerAnswers,
): ScreenerDecision | undefined {
  for (const rule of RULES) {
    const decision = rule(answers);
    if (decision) return decision;
  }
  return undefined;
}

/**
 * Pick the first unanswered question in screener order. Returns
 * `undefined` when every question has an answer.
 */
function nextUnansweredQuestionId(
  answers: IScreenerAnswers,
): string | undefined {
  for (const q of screenerQuestions) {
    if (isAnswered(q, answers)) continue;
    // Skip the J-1 follow-up when the user said "no" on j1_two_year_rule
    // — the question is conditional on the parent being yes. The
    // router routes around the dead branch by treating it as
    // implicitly "na".
    if (q.id === 'j1_two_year_complied' && answers.j1_two_year_rule === false) {
      continue;
    }
    return q.id;
  }
  return undefined;
}

function isAnswered(
  q: IScreenerQuestion,
  answers: IScreenerAnswers,
): boolean {
  const value = (answers as Record<string, unknown>)[q.id];
  if (q.kind === 'multi_select') {
    return Array.isArray(value) && value.length > 0;
  }
  if (q.kind === 'boolean') {
    return typeof value === 'boolean';
  }
  return typeof value === 'string' && value.length > 0;
}

/**
 * The router's public entry point. Given a partial answer set, return
 * either the next question to ask or the terminal outcome.
 *
 * Pure: same input always produces the same decision. The caller is
 * responsible for persisting `answers` (to localStorage on the
 * eligible cross-write, in-memory otherwise).
 */
export function routeScreener(
  answers: IScreenerAnswers,
): ScreenerDecision {
  // Always check rules first — a terminal answer mid-flow short-circuits
  // the remaining questions.
  const terminal = evaluateRules(answers);
  if (terminal) return terminal;

  const nextQ = nextUnansweredQuestionId(answers);
  if (nextQ) return { kind: 'continue', nextQuestionId: nextQ };

  return { kind: 'outcome', outcome: 'eligible', reason: 'all_clean' };
}

/**
 * Convenience helper for tests — builds a "happy path" answer set
 * that produces the eligible outcome. Mutated copies of this drive
 * the property tests below.
 */
export function makeEligibleAnswers(): IScreenerAnswers {
  return {
    filing_path: 'spouse_of_us_citizen',
    inside_united_states: 'inside',
    beneficiary_immigration_status: 'lawful_entry_visa',
    derivative_or_primary: 'primary',
    sponsor_citizenship: 'us_citizen',
    sponsor_income_bracket: 'at_or_above',
    has_marriage_certificate: true,
    j1_two_year_rule: false,
    j1_two_year_complied: 'na',
    immigration_history_bundle: ['none'],
    criminal_public_charge_bundle: ['none'],
    security_severe_harm_bundle: ['none'],
  };
}

/**
 * Compute the 0..1 progress fraction of an in-flight session. Driven
 * by the count of answered questions divided by the count visible
 * given current answers. The J-1 follow-up is excluded from the
 * denominator when the parent question said "no" so a confidently
 * negative answer doesn't make the bar appear stuck.
 */
export function screenerProgressFraction(
  answers: IScreenerAnswers,
): number {
  let answered = 0;
  let total = 0;
  for (const q of screenerQuestions) {
    if (q.id === 'j1_two_year_complied' && answers.j1_two_year_rule === false) {
      continue;
    }
    total += 1;
    if (isAnswered(q, answers)) answered += 1;
  }
  if (total === 0) return 1;
  return answered / total;
}
